import React, { createContext, useContext, useEffect, useState, useRef, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './index.css'
import { supabase, getProfile } from './lib/supabase'

// Pages — every page except Login and the admin shell is code-split so the first load only
// downloads what the visited route needs (the main bundle was ~3 MB with all pages eager).
import Login from './pages/Login'
import AdminShell from './pages/AdminShell'
const OwnerPortal = lazy(() => import('./pages/OwnerPortal'))
const PartnerPortal = lazy(() => import('./pages/PartnerPortal'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Fleet = lazy(() => import('./pages/Fleet'))
const FleetExpenses = lazy(() => import('./pages/fleetops/Expenses'))
const FleetService = lazy(() => import('./pages/fleetops/Service'))
const FleetClaims = lazy(() => import('./pages/fleetops/Claims'))
const HQFleet = lazy(() => import('./pages/HQFleet'))
const HQCarDetail = lazy(() => import('./pages/HQCarDetail'))
const FleetCarDetail = lazy(() => import('./pages/FleetCarDetail'))
const Rentals = lazy(() => import('./pages/Rentals'))
const Bookings = lazy(() => import('./pages/bookings/Bookings'))
const Owners = lazy(() => import('./pages/Owners'))
const Partners = lazy(() => import('./pages/Partners'))
const Reports = lazy(() => import('./pages/Reports'))
const UserManagement = lazy(() => import('./pages/UserManagement'))
const Maintenance = lazy(() => import('./pages/Maintenance'))
const Renters = lazy(() => import('./pages/Renters'))
const RenterDetail = lazy(() => import('./pages/RenterDetail'))
const EmployeePortal = lazy(() => import('./pages/EmployeePortal'))
const EmployeeRentalDetail = lazy(() => import('./pages/EmployeeRentalDetail'))
const FleetCatalog = lazy(() => import('./pages/FleetCatalog'))
const Payouts = lazy(() => import('./pages/Payouts'))
const RentalDetail = lazy(() => import('./pages/RentalDetail'))
const CalendarPage = lazy(() => import('./pages/CalendarPage'))
const BrokerAvailability = lazy(() => import('./pages/BrokerAvailability'))
const LiveMap = lazy(() => import('./pages/LiveMap'))
const PublicQuote = lazy(() => import('./pages/PublicQuote'))
const BrokerRates = lazy(() => import('./pages/BrokerRates'))
const PublicBooking = lazy(() => import('./pages/PublicBooking'))
const PublicGallery = lazy(() => import('./pages/PublicGallery'))
const BrokerFleet = lazy(() => import('./pages/BrokerFleet'))
const ClientOnboarding = lazy(() => import('./pages/ClientOnboarding'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const OnboardingDetail = lazy(() => import('./pages/OnboardingDetail'))
const NewRental = lazy(() => import('./pages/NewRental'))
const NewOnboarding = lazy(() => import('./pages/NewOnboarding'))
const NewVehicle = lazy(() => import('./pages/NewVehicle'))
const NewOwner = lazy(() => import('./pages/NewOwner'))
const NewPartner = lazy(() => import('./pages/NewPartner'))
const NewRenter = lazy(() => import('./pages/NewRenter'))
const PublicCheckout = lazy(() => import('./pages/PublicCheckout'))
const FinishOnboarding = lazy(() => import('./pages/FinishOnboarding'))
const PayRedirect = lazy(() => import('./pages/PayRedirect'))
const PublicGettrxPay = lazy(() => import('./pages/PublicGettrxPay'))
const PublicBalancePay = lazy(() => import('./pages/PublicBalancePay'))
const PublicHoldFeePay = lazy(() => import('./pages/PublicHoldFeePay'))
const BookingRequests = lazy(() => import('./pages/BookingRequests'))
const Settings = lazy(() => import('./pages/Settings'))
const TuroInbox = lazy(() => import('./pages/TuroInbox'))

export const AuthContext = createContext(null)
export const useAuth = () => useContext(AuthContext)

function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined)
  const [profile, setProfile] = useState(undefined)
  const [initialized, setInitialized] = useState(false)
  // Refs let our auth callback see the latest values without re-subscribing
  const initializedRef = useRef(false)
  const lastUserIdRef = useRef(null)

  useEffect(() => {
    let mounted = true

    const loadProfile = async (u) => {
      if (!u) {
        if (mounted) setProfile(null)
        return
      }
      try {
        const p = await getProfile()
        if (mounted) setProfile(p)
      } catch {
        if (mounted) setProfile(null)
      }
    }

    const handleSession = async (session) => {
      if (!mounted) return
      const currentUser = session?.user ?? null
      const currentUserId = currentUser?.id ?? null

      // First event: always handle (initial session restore).
      // Subsequent events: only handle if user identity actually changed.
      // This prevents tab-refocus TOKEN_REFRESHED events from wiping form state.
      const isFirstEvent = !initializedRef.current
      if (!isFirstEvent && currentUserId === lastUserIdRef.current) {
        return
      }

      lastUserIdRef.current = currentUserId
      setUser(currentUser)
      await loadProfile(currentUser)
      if (mounted && !initializedRef.current) {
        initializedRef.current = true
        setInitialized(true)
      }
    }

    // 1. Get the initial session immediately (belt-and-suspenders so we
    //    always initialize, even if onAuthStateChange's INITIAL_SESSION
    //    event is delayed or missed).
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session)
    }).catch(() => {
      // If getSession fails, still mark as initialized so we show Login
      if (mounted && !initializedRef.current) {
        initializedRef.current = true
        setInitialized(true)
      }
    })

    // 2. Subscribe to subsequent auth changes (sign in, sign out, refresh).
    //    Ignores no-op events on the same user (tab refocus protection).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      handleSession(session)
    })

    // 3. Safety timeout — if nothing initializes us within 5 seconds,
    //    force initialized so we at least show the Login page rather than
    //    hanging on "Loading..." forever.
    const safetyTimeout = setTimeout(() => {
      if (mounted && !initializedRef.current) {
        initializedRef.current = true
        setInitialized(true)
      }
    }, 5000)

    return () => {
      mounted = false
      clearTimeout(safetyTimeout)
      subscription.unsubscribe()
    }
  }, [])

  // Only show "Loading…" on the very first load — never on tab refocus
  const loading = !initialized

  return (
    <AuthContext.Provider value={{ user, profile, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

function RequireAuth({ children, role }) {
  const { user, profile, loading } = useAuth()
  if (loading) return <div className="loading">Loading...</div>
  if (!user) return <Navigate to="/login" replace />
  if (role && profile?.role !== role) {
    if (!profile) return <Navigate to="/login" replace />
    // super_admin can access admin routes
    if (role === 'admin' && profile.role === 'super_admin') return children
    // Franchise admins use the same admin shell; RLS scopes them to their location.
    if (role === 'admin' && profile.role === 'franchise_admin') return children
    if (profile.role === 'super_admin' || profile.role === 'admin') return children
    if (profile.role === 'franchise_admin') return <Navigate to="/" replace />
    if (profile.role === 'owner') return <Navigate to="/portal" replace />
    if (profile.role === 'partner') return <Navigate to="/partner-portal" replace />
    if (profile.role === 'employee') return <Navigate to="/employee" replace />
    return <Navigate to="/login" replace />
  }
  return children
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<div className="loading">Loading...</div>}>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route path="/" element={
            <RequireAuth role="admin">
              <AdminShell />
            </RequireAuth>
          }>
            <Route index element={<Dashboard />} />
            <Route path="fleet" element={<Fleet />} />
            <Route path="hq-fleet" element={<HQFleet />} />
            <Route path="hq-fleet/:carId" element={<HQCarDetail />} />
            <Route path="fleet/new" element={<NewVehicle />} />
            <Route path="fleet/expenses" element={<FleetExpenses />} />
            <Route path="fleet/service" element={<FleetService />} />
            <Route path="fleet/claims" element={<FleetClaims />} />
            <Route path="fleet/:carId" element={<FleetCarDetail />} />
            <Route path="rentals" element={<Bookings />} />
            <Route path="rentals/classic" element={<Rentals />} />
            <Route path="rentals/new" element={<NewRental />} />
            <Route path="rentals/:id" element={<RentalDetail />} />
            <Route path="owners" element={<Owners />} />
            <Route path="owners/new" element={<NewOwner />} />
            <Route path="partners" element={<Partners />} />
            <Route path="partners/new" element={<NewPartner />} />
            <Route path="payouts" element={<Payouts />} />
            <Route path="reports" element={<Reports />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="maintenance" element={<Maintenance />} />
            <Route path="renters" element={<Renters />} />
            <Route path="renters/new" element={<NewRenter />} />
            <Route path="renters/:id" element={<RenterDetail />} />
            <Route path="fleet-catalog" element={<FleetCatalog />} />
            <Route path="calendar" element={<Navigate to="/rentals?tab=calendar" replace />} />
            <Route path="calendar/classic" element={<CalendarPage />} />
            <Route path="live-map" element={<LiveMap />} />
            <Route path="broker-availability" element={<BrokerAvailability />} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="onboarding/new" element={<NewOnboarding />} />
            <Route path="onboarding/:id" element={<OnboardingDetail />} />
            <Route path="bookings" element={<BookingRequests />} />
            <Route path="turo" element={<TuroInbox />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="/employee" element={<RequireAuth role="employee"><EmployeePortal /></RequireAuth>} />
          <Route path="/employee/rental/:id" element={<RequireAuth role="employee"><EmployeeRentalDetail /></RequireAuth>} />

          <Route path="/portal" element={
            <RequireAuth role="owner">
              <OwnerPortal />
            </RequireAuth>
          } />

          <Route path="/partner-portal" element={
            <RequireAuth role="partner">
              <PartnerPortal />
            </RequireAuth>
          } />

          {/* Public routes — no auth required */}
          <Route path="/quote/:token" element={<PublicQuote />} />
          <Route path="/broker-rates/:token" element={<BrokerRates />} />
          <Route path="/onboard/:token" element={<ClientOnboarding />} />
          <Route path="/book/:carId" element={<PublicBooking />} />
          <Route path="/checkout/:carId" element={<PublicCheckout />} />
          <Route path="/finish/:token" element={<FinishOnboarding />} />
          <Route path="/pay/:slug" element={<PayRedirect />} />
          <Route path="/payment/:token" element={<PublicGettrxPay />} />
          <Route path="/balance/:token" element={<PublicBalancePay />} />
          <Route path="/holdfee/:token" element={<PublicHoldFeePay />} />
          <Route path="/gallery/:carId" element={<PublicGallery />} />
          <Route path="/v/:token" element={<PublicGallery />} />
          <Route path="/broker/:slug" element={<BrokerFleet />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  )
}
